package songwritersnotebook.app.utils

import songwritersnotebook.app.viewmodels.EditorViewModel

class HarmonyActionExecutor(private val viewModel: EditorViewModel) {
    
    fun execute(toolName: String, args: kotlinx.serialization.json.JsonElement) {
        try {
            when (toolName) {
                "insert_chord_into_lyrics" -> {
                    // Extract fields from JsonElement (args is a JsonObject)
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    val wordIndex = jsonObject["wordIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    val chordName = jsonObject["chordName"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    
                    viewModel.insertHarmonyChord(sectionId, lineIndex, wordIndex, chordName)
                }
                
                "add_chord_to_library" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val name = jsonObject["name"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return

                    val pianoNotesArray = jsonObject["pianoNotes"] as? kotlinx.serialization.json.JsonArray
                    val pianoNotes = pianoNotesArray?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content } ?: emptyList()

                    val guitarTabArray = jsonObject["guitarTab"] as? kotlinx.serialization.json.JsonArray
                    val guitarTabStr = guitarTabArray?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content } ?: emptyList()

                    // Convert string representation of 'x' or '0'-'24' to GuitarTab expected format (String)
                    val guitarTab = guitarTabStr.map { fretStr ->
                        if (fretStr.lowercase() == "x") "x" else (fretStr.toIntOrNull() ?: 0).toString()
                    }

                    viewModel.addHarmonyChordToLibrary(name, pianoNotes, guitarTab)
                }

                "identify_chord_from_audio" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val chordName = jsonObject["chordName"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    println("DEBUG_HARMONY: Client-side identified chord: $chordName")
                    // In the future, we could trigger a UI highlight or specific animation here.
                }                
                "add_lyric_line" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val text = jsonObject["text"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    
                    viewModel.addHarmonyLyricLine(sectionId, text)
                }
                "delete_section" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyDeleteSection(sectionId)
                }

                "delete_lyric_line" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    viewModel.harmonyDeleteLyricLine(sectionId, lineIndex)
                }

                "update_lyric_line" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    val text = jsonObject["text"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyUpdateLyricLine(sectionId, lineIndex, text)
                }

                "remove_chord" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    val chordName = jsonObject["chordName"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyRemoveChord(sectionId, lineIndex, chordName)
                }

                "rename_section" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val newName = jsonObject["newName"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyRenameSection(sectionId, newName)
                }

                "change_bpm" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val bpm = jsonObject["bpm"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    viewModel.harmonyChangeBpm(bpm)
                }

                "update_song_title" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val title = jsonObject["title"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyUpdateSongTitle(title)
                }

                "change_capo" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val capo = jsonObject["capo"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    viewModel.harmonyChangeCapo(capo)
                }

                "update_chord_in_library" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val chordName = jsonObject["chordName"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    
                    val newName = jsonObject["newName"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content
                    
                    val pianoNotesArray = jsonObject["newPianoNotes"] as? kotlinx.serialization.json.JsonArray
                    val newPianoNotesStr = pianoNotesArray?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }
                    
                    val guitarTabArray = jsonObject["newGuitarTab"] as? kotlinx.serialization.json.JsonArray
                    val newGuitarTabStr = guitarTabArray?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }
                    
                    viewModel.harmonyUpdateChordInLibrary(chordName, newName, newPianoNotesStr, newGuitarTabStr)
                }

                "start_recording" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull()
                    
                    viewModel.harmonyStartRecording(sectionId, lineIndex)
                }

                "change_tuning" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val tuningArray = jsonObject["tuning"] as? kotlinx.serialization.json.JsonArray
                    val tuning = tuningArray?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content } ?: return
                    
                    viewModel.harmonyChangeTuning(tuning)
                }

                "reset_tuning_to_standard" -> {
                    viewModel.resetTuningToStandard()
                }

                "navigate_to_studio" -> {
                    viewModel.harmonyNavigateToStudio()
                }

                "open_word_finder" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val searchTerm = jsonObject["searchTerm"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content
                    viewModel.harmonyOpenWordFinder(searchTerm)
                }

                "toggle_metronome" -> {
                    viewModel.toggleMetronomeEnabled()
                }

                "set_metronome_mute" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val muted = jsonObject["muted"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toBooleanStrictOrNull() ?: return
                    viewModel.harmonySetMetronomeMute(muted)
                }

                "change_setting" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val settingName = jsonObject["settingName"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val value = jsonObject["value"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toBooleanStrictOrNull() ?: return
                    viewModel.harmonyChangeSetting(settingName, value)
                }

                "add_section" -> {
                    viewModel.addSection()
                }

                "move_item" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val itemId = jsonObject["itemId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val direction = jsonObject["direction"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyMoveItem(itemId, direction)
                }

                "duplicate_item" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val itemId = jsonObject["itemId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyDuplicateItem(itemId)
                }

                "delete_item" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val itemId = jsonObject["itemId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyDeleteItem(itemId)
                }

                "reorder_sections" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionIdsArray = jsonObject["sectionIds"] as? kotlinx.serialization.json.JsonArray
                    val sectionIds = sectionIdsArray?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content } ?: return
                    viewModel.reorderSections(sectionIds)
                }

                "get_rhymes" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val word = jsonObject["word"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyGetRhymes(word)
                }

                "transpose_song" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val semitones = jsonObject["semitones"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    
                    val newDefsArray = jsonObject["newChordDefinitions"] as? kotlinx.serialization.json.JsonArray
                    val newDefs = newDefsArray?.mapNotNull { def ->
                        val obj = def as? kotlinx.serialization.json.JsonObject ?: return@mapNotNull null
                        val name = (obj["name"] as? kotlinx.serialization.json.JsonPrimitive)?.content ?: return@mapNotNull null
                        
                        val pianoNotes = (obj["pianoNotes"] as? kotlinx.serialization.json.JsonArray)?.mapNotNull { 
                            (it as? kotlinx.serialization.json.JsonPrimitive)?.content 
                        }
                        val guitarTab = (obj["guitarTab"] as? kotlinx.serialization.json.JsonArray)?.mapNotNull { 
                            (it as? kotlinx.serialization.json.JsonPrimitive)?.content 
                        }
                        
                        songwritersnotebook.app.data.ChordDefinition(
                            name = name,
                            pianoNotes = pianoNotes,
                            guitarTab = guitarTab,
                            isCustom = true
                        )
                    } ?: emptyList()

                    viewModel.transposeSong(semitones, newDefs)
                }

                "create_chord_progression" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val name = jsonObject["name"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: "AI Progression"
                    
                    val chordNamesArray = jsonObject["chordNames"] as? kotlinx.serialization.json.JsonArray
                    val chordNames = chordNamesArray?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content } ?: emptyList()
                    
                    val newDefsArray = jsonObject["newChordDefinitions"] as? kotlinx.serialization.json.JsonArray
                    val newDefs = newDefsArray?.mapNotNull { def ->
                        val obj = def as? kotlinx.serialization.json.JsonObject ?: return@mapNotNull null
                        val chordName = (obj["name"] as? kotlinx.serialization.json.JsonPrimitive)?.content ?: return@mapNotNull null
                        
                        val pianoNotes = (obj["pianoNotes"] as? kotlinx.serialization.json.JsonArray)?.mapNotNull { 
                            (it as? kotlinx.serialization.json.JsonPrimitive)?.content 
                        }
                        val guitarTab = (obj["guitarTab"] as? kotlinx.serialization.json.JsonArray)?.mapNotNull { 
                            (it as? kotlinx.serialization.json.JsonPrimitive)?.content 
                        }
                        
                        songwritersnotebook.app.data.ChordDefinition(
                            name = chordName,
                            pianoNotes = pianoNotes,
                            guitarTab = guitarTab,
                            isCustom = true
                        )
                    } ?: emptyList()

                    // Add new chords to library first
                    newDefs.forEach { def ->
                        viewModel.addHarmonyChordToLibrary(def.name, def.pianoNotes ?: emptyList(), def.guitarTab ?: emptyList())
                    }
                    
                    val keyRoot = jsonObject["keyRoot"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content
                    val isMajor = jsonObject["isMajor"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toBooleanStrictOrNull()

                    // Save the progression
                    val prog = songwritersnotebook.app.data.ChordProgression(
                        name = name,
                        chordNames = chordNames,
                        keyRoot = keyRoot,
                        isMajor = isMajor
                    )
                    viewModel.saveProgression(prog)
                }

                "update_chord_progression" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val progressionId = jsonObject["progressionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val name = jsonObject["name"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content
                    
                    val chordNamesArray = jsonObject["chordNames"] as? kotlinx.serialization.json.JsonArray
                    val chordNames = chordNamesArray?.mapNotNull { (it as? kotlinx.serialization.json.JsonPrimitive)?.content }
                    
                    val keyRoot = jsonObject["keyRoot"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content
                    val isMajor = jsonObject["isMajor"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toBooleanStrictOrNull()
                    
                    val newDefsArray = jsonObject["newChordDefinitions"] as? kotlinx.serialization.json.JsonArray
                    val newDefs = newDefsArray?.mapNotNull { def ->
                        val obj = def as? kotlinx.serialization.json.JsonObject ?: return@mapNotNull null
                        val chordName = (obj["name"] as? kotlinx.serialization.json.JsonPrimitive)?.content ?: return@mapNotNull null
                        val pianoNotes = (obj["pianoNotes"] as? kotlinx.serialization.json.JsonArray)?.mapNotNull { 
                            (it as? kotlinx.serialization.json.JsonPrimitive)?.content 
                        }
                        val guitarTab = (obj["guitarTab"] as? kotlinx.serialization.json.JsonArray)?.mapNotNull { 
                            (it as? kotlinx.serialization.json.JsonPrimitive)?.content 
                        }
                        songwritersnotebook.app.data.ChordDefinition(
                            name = chordName, pianoNotes = pianoNotes, guitarTab = guitarTab, isCustom = true
                        )
                    } ?: emptyList()

                    newDefs.forEach { def ->
                        viewModel.addHarmonyChordToLibrary(def.name, def.pianoNotes ?: emptyList(), def.guitarTab ?: emptyList())
                    }
                    
                    viewModel.updateProgression(progressionId, name, chordNames, keyRoot, isMajor)
                }

                "delete_chord_progression" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val progressionId = jsonObject["progressionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.deleteProgression(progressionId)
                }

                "rename_recording" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val recId = jsonObject["recordingId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val newLabel = jsonObject["newLabel"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.renameRecording(recId, newLabel)
                }

                "remove_chord_from_library" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val chordName = jsonObject["chordName"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyDeleteChordFromLibrary(chordName)
                }

                "set_metronome" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val enabled = jsonObject["enabled"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toBooleanStrictOrNull() ?: return
                    viewModel.setMetronomeEnabled(enabled)
                }

                "set_instrument" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val instrument = jsonObject["instrument"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.setInstrument(instrument.lowercase())
                }

                "save_song" -> {
                    viewModel.saveSong()
                }

                "split_line" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    val cursorPosition = jsonObject["cursorPosition"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    viewModel.harmonySplitLine(sectionId, lineIndex, cursorPosition)
                }

                "merge_line_with_previous" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull() ?: return
                    viewModel.harmonyMergeLineWithPrevious(sectionId, lineIndex)
                }

                "merge_section_with_previous" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyMergeSectionWithPrevious(sectionId)
                }

                "play_chord" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val chordName = jsonObject["chordName"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.playChordByName(chordName)
                }

                "set_chord_display_mode" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val mode = jsonObject["mode"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonySetChordDisplayMode(mode)
                }

                "toggle_show_notes" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val show = jsonObject["show"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toBooleanStrictOrNull()
                    viewModel.harmonyToggleShowNotes(show)
                }

                "update_notes" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val content = jsonObject["content"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.updateNotes(content)
                }

                "delete_recording" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val recordingId = jsonObject["recordingId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyDeleteRecording(recordingId)
                }

                "play_recording" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val recordingId = jsonObject["recordingId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    viewModel.harmonyPlayRecording(recordingId)
                }

                "stop_playback" -> {
                    viewModel.stopPlayback()
                }

                "open_media_for_line" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull()
                    viewModel.harmonyOpenMediaForLine(sectionId, lineIndex)
                }

                "add_guitar_tab" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull()
                    
                    viewModel.harmonyOpenTabEditor(sectionId, lineIndex)
                }

                "update_guitar_tab" -> {
                    val jsonObject = args as? kotlinx.serialization.json.JsonObject ?: return
                    val tabId = jsonObject["tabId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val sectionId = jsonObject["sectionId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content ?: return
                    val lineIndex = jsonObject["lineIndex"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull()
                    
                    val name = jsonObject["name"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content
                    val beats = jsonObject["beats"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull()
                    val subdivisions = jsonObject["subdivisions"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content?.toIntOrNull()
                    val recordingId = jsonObject["recordingId"]?.let { it as? kotlinx.serialization.json.JsonPrimitive }?.content

                    val columnsArray = jsonObject["columns"] as? kotlinx.serialization.json.JsonArray
                    val columns = columnsArray?.map { col ->
                        (col as? kotlinx.serialization.json.JsonArray)?.map { (it as kotlinx.serialization.json.JsonPrimitive).content } ?: emptyList<String>()
                    }
                    
                    viewModel.harmonyUpdateGuitarTab(
                        tabId = tabId,
                        sectionId = sectionId,
                        lineIndex = lineIndex,
                        name = name,
                        beats = beats,
                        subdivisions = subdivisions,
                        columns = columns,
                        recordingId = recordingId
                    )
                }

                else -> {
                    println("harmony_action_executor: Unknown tool call: $toolName")
                }
            }
        } catch (e: Exception) {
            println("harmony_action_executor: Error parsing tool args for $toolName -> ${e.message}")
        }
    }
}
